import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import {
  doc, getDoc, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { uploadToCloudinary, uploadCertificateToCloudinary } from '../../config/cloudinary';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../Shared/Sidebar';

export default function EditStudent() {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [useCustomClass, setUseCustomClass] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    class: '',
    school: '',
    region: '',
    district: '',
    ward: '',
    parentName: '',
    parentPhone: '',
    teacherName: '',
    teacherPhone: '',
    teacherEmail: '',
    teacherFeedback: '',
    healthStatus: '',
    notes: '',
    hasBirthCertificate: '',
    photoURL: '',
    certificateURL: '',
  });

  useEffect(() => {
    fetchStudent();
  }, [id]);

  async function fetchStudent() {
    try {
      const docRef = doc(db, 'students', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setForm({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          dateOfBirth: data.dateOfBirth || '',
          gender: data.gender || '',
          class: data.class || '',
          school: data.school || '',
          region: data.region || '',
          district: data.district || '',
          ward: data.ward || '',
          parentName: data.parentName || '',
          parentPhone: data.parentPhone || '',
          teacherName: data.teacherName || '',
          teacherPhone: data.teacherPhone || '',
          teacherEmail: data.teacherEmail || '',
          teacherFeedback: data.teacherFeedback || '',
          healthStatus: data.healthStatus || '',
          notes: data.notes || '',
          hasBirthCertificate: data.hasBirthCertificate || '',
          photoURL: data.photoURL || '',
          certificateURL: data.certificateURL || '',
        });
        if (data.photoURL) setPhotoPreview(data.photoURL);
        if (data.class && !['1','2','3','4','5','6','7'].includes(data.class)) {
          setUseCustomClass(true);
        }
        if (data.certificateURL) {
          if (data.certificateURL.includes('.pdf')) {
            setCertificatePreview('pdf');
          } else {
            setCertificatePreview(data.certificateURL);
          }
        }
      } else {
        setError('Student not found!');
      }
    } catch (err) {
      console.error(err);
      setError('Could not load student data.');
    }
    setFetching(false);
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function handlePhoto(e) {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  }

  function handleCertificate(e) {
    const file = e.target.files[0];
    if (file) {
      setCertificate(file);
      if (file.type.startsWith('image/')) {
        setCertificatePreview(URL.createObjectURL(file));
      } else {
        setCertificatePreview('pdf');
      }
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let photoURL = form.photoURL;
      let certificateURL = form.certificateURL;

      if (photo) {
        photoURL = await uploadToCloudinary(photo);
      }
      if (certificate) {
        certificateURL = await uploadCertificateToCloudinary(
          certificate
        );
      }

      await updateDoc(doc(db, 'students', id), {
        ...form,
        photoURL,
        certificateURL,
        updatedBy: currentUser.uid,
        updatedAt: serverTimestamp(),
      });

      setSuccess('Student updated successfully!');
      setTimeout(() => navigate(-1), 2000);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    }
    setLoading(false);
  }

  if (fetching) {
    return (
      <div style={styles.layout}>
        <Sidebar />
        <div style={styles.main}>
          <div style={styles.loading}>
            Loading student data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>
              Edit Student
            </div>
            <div style={styles.pageSub}>
              {form.firstName} {form.lastName}
            </div>
          </div>
          <button
            style={styles.btnOutline}
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
        </div>

        {success && (
          <div style={styles.successMsg}>{success}</div>
        )}
        {error && (
          <div style={styles.errorMsg}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>

          {/* PHOTO */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Student Photo
            </div>
            <div style={styles.photoArea}>
              <div style={styles.photoPreview}>
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={styles.photoImg}
                  />
                ) : (
                  <span style={styles.photoPlaceholder}>
                    
                  </span>
                )}
              </div>
              <div>
                <label style={styles.uploadBtn}>
                  Change Photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhoto}
                    style={{ display: 'none' }}
                  />
                </label>
                <div style={styles.uploadHint}>
                  JPG or PNG · Max 5MB
                </div>
              </div>
            </div>
          </div>

          {/* PERSONAL INFO */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Personal Information
            </div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  First Name *
                </label>
                <input
                  style={styles.formInput}
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Last Name *
                </label>
                <input
                  style={styles.formInput}
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Date of Birth *
                </label>
                <input
                  style={styles.formInput}
                  name="dateOfBirth"
                  type="date"
                  value={form.dateOfBirth}
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Gender *
                </label>
                <select
                  style={styles.formInput}
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select gender</option>
                  <option>Male</option>
                  <option>Female</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Class *
                </label>
                <select
                  style={styles.formInput}
                  name="class"
                  value={
                    useCustomClass
                      ? 'other'
                      : form.class
                  }
                  onChange={(e) => {
                    if (e.target.value === 'other') {
                      setUseCustomClass(true);
                      setForm({ ...form, class: '' });
                    } else {
                      setUseCustomClass(false);
                      setForm({ ...form, class: e.target.value });
                    }
                  }}
                  required={!useCustomClass}
                >
                  <option value="">Select class</option>
                  <option value="1">Class 1</option>
                  <option value="2">Class 2</option>
                  <option value="3">Class 3</option>
                  <option value="4">Class 4</option>
                  <option value="5">Class 5</option>
                  <option value="6">Class 6</option>
                  <option value="7">Class 7</option>
                  <option value="other">Other (type manually)</option>
                </select>
                {useCustomClass && (
                  <input
                    style={{ ...styles.formInput, marginTop: '8px' }}
                    name="class"
                    value={form.class}
                    onChange={handleChange}
                    placeholder="e.g. Maandalizi, Standard 8"
                    required
                    autoFocus
                  />
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  School Name *
                </label>
                <input
                  style={styles.formInput}
                  name="school"
                  value={form.school}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* CLASS TEACHER */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Class Teacher
            </div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Teacher Full Name *
                </label>
                <input
                  style={styles.formInput}
                  name="teacherName"
                  value={form.teacherName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Teacher Phone
                </label>
                <input
                  style={styles.formInput}
                  name="teacherPhone"
                  value={form.teacherPhone}
                  onChange={handleChange}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Teacher Email
                </label>
                <input
                  style={styles.formInput}
                  name="teacherEmail"
                  value={form.teacherEmail}
                  onChange={handleChange}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Feedback from Teacher
                </label>
                <input
                  style={styles.formInput}
                  name="teacherFeedback"
                  value={form.teacherFeedback}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* LOCATION */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>Location</div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Region *
                </label>
                <input
                  style={styles.formInput}
                  name="region"
                  value={form.region}
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  District *
                </label>
                <input
                  style={styles.formInput}
                  name="district"
                  value={form.district}
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Ward</label>
                <input
                  style={styles.formInput}
                  name="ward"
                  value={form.ward}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* PARENT */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Parent / Guardian
            </div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Parent Full Name *
                </label>
                <input
                  style={styles.formInput}
                  name="parentName"
                  value={form.parentName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Parent Phone *
                </label>
                <input
                  style={styles.formInput}
                  name="parentPhone"
                  value={form.parentPhone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* BIRTH CERTIFICATE */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Birth Certificate
            </div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Birth Certificate Status *
                </label>
                <select
                  style={styles.formInput}
                  name="hasBirthCertificate"
                  value={form.hasBirthCertificate}
                  onChange={handleChange}
                >
                  <option value="">Select option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="processing">In process</option>
                </select>
              </div>
            </div>
            {form.hasBirthCertificate === 'yes' && (
              <div style={{ marginTop: '16px' }}>
                <label style={styles.uploadBtn}>
                  Change Certificate
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleCertificate}
                    style={{ display: 'none' }}
                  />
                </label>
                {certificatePreview && (
                  <div style={{ marginTop: '12px' }}>
                    {certificatePreview === 'pdf' ? (
                      <div style={styles.pdfPreview}>
                        Certificate uploaded 
                      </div>
                    ) : (
                      <img
                        src={certificatePreview}
                        alt="Certificate"
                        style={styles.certImg}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* HEALTH */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Health & Notes
            </div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Health Status
                </label>
                <select
                  style={styles.formInput}
                  name="healthStatus"
                  value={form.healthStatus}
                  onChange={handleChange}
                >
                  <option value="">Select status</option>
                  <option>Healthy</option>
                  <option>Needs Attention</option>
                  <option>Under Treatment</option>
                </select>
              </div>
              <div style={{
                ...styles.formGroup,
                gridColumn: '1 / -1'
              }}>
                <label style={styles.formLabel}>
                  Additional Notes
                </label>
                <textarea
                  style={{
                    ...styles.formInput,
                    height: '90px',
                    resize: 'vertical'
                  }}
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            style={styles.submitBtn}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>

        </form>
      </div>
    </div>
  );
}

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#F6F4EE' },
  main: { flex: 1, padding: '26px', overflowY: 'auto' },
  loading: { textAlign: 'center', padding: '60px', fontSize: '16px', color: '#6B7280' },
  pageHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '24px',
    flexWrap: 'wrap', gap: '14px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif", fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  pageSub: { fontSize: '13px', color: '#6B7280', marginTop: '3px' },
  btnOutline: {
    background: 'transparent', color: '#14452F',
    border: '1.5px solid #1D5C3C', padding: '10px 20px',
    borderRadius: '10px', fontFamily: 'inherit',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  successMsg: {
    background: '#EAF3EC', border: '1px solid #BBD8C4',
    color: '#14452F', padding: '12px 16px',
    borderRadius: '10px', fontSize: '13px', marginBottom: '20px',
  },
  errorMsg: {
    background: '#FFEBEE', border: '1px solid #FFCDD2',
    color: '#C62828', padding: '12px 16px',
    borderRadius: '10px', fontSize: '13px', marginBottom: '20px',
  },
  panel: {
    background: '#fff', borderRadius: '16px', padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '11px', letterSpacing: '0.8px',
    textTransform: 'uppercase', color: '#6B7280',
    marginBottom: '14px', paddingBottom: '8px',
    borderBottom: '1px solid #F3F4F6', fontWeight: '600',
  },
  photoArea: { display: 'flex', alignItems: 'center', gap: '20px' },
  photoPreview: {
    width: '80px', height: '80px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', flexShrink: 0,
  },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  photoPlaceholder: { fontSize: '32px' },
  uploadBtn: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    color: '#fff', padding: '9px 18px', borderRadius: '9px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  uploadHint: { fontSize: '11.5px', color: '#6B7280', marginTop: '6px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
  formLabel: { fontSize: '12px', color: '#6B7280', fontWeight: '500' },
  formInput: {
    padding: '11px 14px', borderRadius: '9px',
    border: '1.5px solid #E5E7EB', background: '#FAF9F5',
    fontFamily: 'inherit', fontSize: '13.5px',
    color: '#1C1C1E', outline: 'none',
  },
  pdfPreview: {
    background: '#EAF3EC', border: '1px solid #BBD8C4',
    color: '#14452F', padding: '12px 16px',
    borderRadius: '10px', fontSize: '13px',
  },
  certImg: {
    width: '100%', maxWidth: '300px',
    borderRadius: '10px', border: '1px solid #E5E7EB',
  },
  submitBtn: {
    width: '100%', padding: '15px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff', border: 'none', borderRadius: '12px',
    fontFamily: 'inherit', fontSize: '15px', fontWeight: '600',
    cursor: 'pointer', boxShadow: '0 6px 20px rgba(20,69,47,0.3)',
    marginBottom: '30px',
  },
};